import type {
  Project,
  RelationsStore,
  WithId,
} from '@octopus-do/octopus-project-core'
import { createAction } from '@reduxjs/toolkit'
import type { LoaderType } from '@store/loaders/types'
import type { ProjectMeta } from '@store/reducers/manager/meta/types'
import { SET_COMMENTS } from '@storeOld/actions'

import type { ProjectOrigin } from '@/types'

interface FetchProjectOptions {
  silent: boolean
}

type FetchProjectSagaPayload = WithId & Partial<FetchProjectOptions>

// Where to land once the project is loaded: the project itself, or a place
// inside it (a discussion from the activity list)
interface OpenProjectTarget extends WithId {
  path: string
}

// The loader to hold while loading, the project id by default: an activity
// row has its own, the project's other rows stay as they are
interface OpenProjectLoader {
  loader: LoaderType
}

type OpenProjectSagaPayload = OpenProjectTarget & Partial<OpenProjectLoader>

export interface SaveProjectSagaPayload {
  popupDownloadSitemapXml?: boolean
  teamId?: string | null
  ignoreAuthenticated?: boolean
  origin?: ProjectOrigin
  // Flow has no UI of its own to fall back to (e.g. /promo-crawler): when the
  // project limit is reached, create the project as a trial instead of bailing,
  // so the user still lands on a project and sees the upsell there.
  fallbackToTrialOnLimit?: boolean
}

type LoadProjectCommentsPayload = {
  block: []
  node: []
}

interface CreateProjectPayload {
  teamId: string | null
}

type UpdateProjectAndRelationsPayload = {
  project: Project
  relations: RelationsStore
}

export const managerActions = {
  fetchProjectSaga: createAction<FetchProjectSagaPayload>('FETCH_PROJECT_SAGA'),
  openProjectSaga: createAction<OpenProjectSagaPayload>('OPEN_PROJECT_SAGA'),
  refetchProjectAccessSaga: createAction('REFETCH_PROJECT_ACCESS_SAGA'),
  saveProjectSaga: createAction<SaveProjectSagaPayload>('SAVE_PROJECT_SAGA'),

  // create project
  createProjectBoilerplateSaga: createAction('CREATE_PROJECT_BOILERPLATE_SAGA'),
  createProjectAndSaveSaga: createAction<CreateProjectPayload>(
    'CREATE_PROJECT_AND_SAVE_SAGA'
  ),

  // create project by list of actions
  initProjectActionsSaga: createAction('INIT_PROJECT_ACTION_SAGA'),
  initEmptyProjectActionsSaga: createAction('INIT_EMPTY_PROJECT_ACTION_SAGA'),

  loadProject: createAction<any>('LOAD_PROJECT'),
  updateProject: createAction<Project>('UPDATE_PROJECT'),
  loadProjectComments: createAction<LoadProjectCommentsPayload>(
    'LOAD_PROJECT_COMMENTS'
  ),
  resetProject: createAction('RESET_PROJECT'),

  // this action used in root reducer, because relations store is not a part of project manager (but should be)
  updateProjectAndRelations: createAction<UpdateProjectAndRelationsPayload>(
    'UPDATE_PROJECT_AND_RELATIONS'
  ),
  setComments: createAction(SET_COMMENTS),
  updateMeta: createAction<Partial<ProjectMeta>>('UPDATE_META'),
  previewInit: createAction<WithId>('PREVIEW_INIT_SAGA'),
  touch: createAction<WithId>('TOUCH_PROJECT_SAGA'),
}
